/**
 * Geocoding Service for TeeSync
 * 
 * Converts golf course names and addresses to coordinates for weather integration.
 * Uses database lookups with in-memory caching for performance.
 */

// Weather cache import removed - no longer caching geocoding results
import { supabase } from '../lib/supabase.js';

// Cache for database coordinate lookups to avoid repeated queries
const coordinateCache = new Map();

// Fallback coordinates for common golf courses (when database lookup fails)
const COURSE_COORDINATE_FALLBACKS = {
  // UK Golf Courses
  'leyland golf club': { lat: 53.6886, lon: -2.6793, country: 'GB' },
  'st andrews golf club': { lat: 56.3477, lon: -2.7939, country: 'GB' },
  'royal liverpool golf club': { lat: 53.3956, lon: -3.1887, country: 'GB' },
  'royal birkdale golf club': { lat: 53.6464, lon: -3.1947, country: 'GB' },
  'wentworth golf club': { lat: 51.4050, lon: -0.5833, country: 'GB' },
  
  // US Golf Courses
  'augusta national golf club': { lat: 33.5030, lon: -82.0198, country: 'US' },
  'pebble beach golf links': { lat: 36.5683, lon: -121.9461, country: 'US' },
  'pinehurst resort': { lat: 35.1895, lon: -79.4689, country: 'US' },
  'bethpage black': { lat: 40.7435, lon: -73.4579, country: 'US' },
  'torrey pines golf course': { lat: 32.8851, lon: -117.2394, country: 'US' }
};

/**
 * Get fallback coordinates for well-known golf courses
 */
function getFallbackCoordinates(courseName) {
  const normalized = normalizeCourseName(courseName);
  
  // Check for exact match
  if (COURSE_COORDINATE_FALLBACKS[normalized]) {
    return COURSE_COORDINATE_FALLBACKS[normalized];
  }
  
  // Check for partial matches
  for (const [key, coords] of Object.entries(COURSE_COORDINATE_FALLBACKS)) {
    if (normalized.includes(key) || key.includes(normalized)) {
      return coords;
    }
  }
  
  return null;
}

/**
 * Normalize golf course name for lookup
 */
function normalizeCourseName(name) {
  return name
    .toLowerCase()
    .replace(/golf\s*(club|course)/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Check if coordinates are reasonable for a golf course
 */
function validateCoordinates(lat, lon) {
  return lat != null && lon != null &&
         typeof lat === 'number' && typeof lon === 'number' &&
         lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

/**
 * Get coordinates from golf_clubs database table
 */
async function getDatabaseCourseCoordinates(courseName) {
  try {
    // Check cache first
    const cacheKey = normalizeCourseName(courseName);
    if (coordinateCache.has(cacheKey)) {
      const cached = coordinateCache.get(cacheKey);
      return cached;
    }

    // Query database for exact match first
    let { data, error } = await supabase
      .from('golf_clubs')
      .select('latitude, longitude, country_code')
      .eq('name', courseName)
      .not('latitude', 'is', null)
      .not('longitude', 'is', null)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.warn('Database query error:', error);
    }

    // If no exact match, try fuzzy search using ilike
    if (!data) {
      const { data: fuzzyData, error: fuzzyError } = await supabase
        .from('golf_clubs')
        .select('name, latitude, longitude, country_code')
        .ilike('name', `%${courseName}%`)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .limit(1);

      if (fuzzyError) {
        console.warn('Fuzzy search error:', fuzzyError);
        return null;
      }

      if (fuzzyData && fuzzyData.length > 0) {
        data = fuzzyData[0];
      }
    } else {
    }

    if (data && data.latitude && data.longitude) {
      const coordinates = {
        lat: parseFloat(data.latitude),
        lon: parseFloat(data.longitude),
        country: data.country_code || null
      };
      
      // Cache the result
      coordinateCache.set(cacheKey, coordinates);
      
      return coordinates;
    }

    // Cache null result to avoid repeated queries
    coordinateCache.set(cacheKey, null);
    return null;

  } catch (error) {
    console.error('Error fetching coordinates from database:', error);
    return null;
  }
}


/**
 * Get coordinates for a golf course name
 */
export async function getGolfCourseCoordinates(courseName) {
  try {
    if (!courseName || typeof courseName !== 'string') {
      return null; // Return null for missing course names instead of throwing error
    }
    
    const trimmedName = courseName.trim();
    if (trimmedName.length === 0) {
      return null; // Return null for empty course names instead of throwing error
    }
    
    // Skip geocoding for placeholder values
    if (trimmedName.toLowerCase() === 'tbd' ||
        trimmedName.toLowerCase() === 'unknown') {
      return null;
    }
    
    // Check database for golf course coordinates (no caching)
    const dbCoords = await getDatabaseCourseCoordinates(trimmedName);
    if (dbCoords) {
      return dbCoords;
    }
    
    // Try fallback coordinates for well-known courses
    const fallbackCoords = getFallbackCoordinates(trimmedName);
    if (fallbackCoords) {
      return fallbackCoords;
    }
    
    // No coordinates found anywhere
    return null;
    
  } catch (error) {
    console.error('Error getting golf course coordinates:', error);
    throw error;
  }
}

/**
 * Batch geocode multiple golf course names
 */
export async function batchGeocode(courseNames, options = {}) {
  const { maxConcurrent = 3, delayMs = 100 } = options;
  const results = [];
  
  // Process in batches to avoid rate limiting
  for (let i = 0; i < courseNames.length; i += maxConcurrent) {
    const batch = courseNames.slice(i, i + maxConcurrent);
    
    const batchPromises = batch.map(async (courseName, index) => {
      try {
        // Add delay to avoid hitting rate limits
        if (index > 0) {
          await new Promise(resolve => setTimeout(resolve, delayMs * index));
        }
        
        const result = await getGolfCourseCoordinates(courseName, options);
        return { courseName, result, error: null };
      } catch (error) {
        return { courseName, result: null, error: error.message };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    // Delay between batches
    if (i + maxConcurrent < courseNames.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs * maxConcurrent));
    }
  }
  
  return results;
}

/**
 * Validate and format coordinates
 */
export function formatCoordinates(coords) {
  if (!coords || !validateCoordinates(coords.lat, coords.lon)) {
    return null;
  }
  
  return {
    lat: parseFloat(coords.lat.toFixed(4)),
    lon: parseFloat(coords.lon.toFixed(4)),
    formatted: `${coords.lat.toFixed(4)}, ${coords.lon.toFixed(4)}`
  };
}

/**
 * Get distance between two coordinate points (in kilometers)
 */
export function getDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Check if geocoding is available
 */
export function isGeocodingAvailable() {
  return true;
}

/**
 * Add a golf course to the database with coordinates
 */
export async function addKnownCourse(name, coordinates) {
  try {
    if (!validateCoordinates(coordinates.lat, coordinates.lon)) {
      return false;
    }

    // Skip if a row with this (case-insensitive) name already exists. The
    // legacy UNIQUE(name) constraint was dropped to allow multi-course rows,
    // so we explicit-check instead of relying on ON CONFLICT.
    const { data: existing } = await supabase
      .from('golf_clubs')
      .select('id')
      .ilike('name', name.trim())
      .is('course_name', null)
      .limit(1)
      .maybeSingle();

    if (!existing) {
      const { error } = await supabase
        .from('golf_clubs')
        .insert([{
          name: name.trim(),
          latitude: coordinates.lat,
          longitude: coordinates.lon,
          country_code: coordinates.country || null
        }]);

      if (error) {
        console.error('Error adding golf course:', error);
        return false;
      }
    }

    // Clear the cache for this course name
    const cacheKey = normalizeCourseName(name);
    coordinateCache.delete(cacheKey);

    return true;
  } catch (error) {
    console.error('Error adding known course:', error);
    return false;
  }
}

/**
 * Get all golf courses with coordinates from database
 */
export async function getKnownCourses() {
  try {
    const { data, error } = await supabase
      .from('golf_clubs')
      .select('name, latitude, longitude, country_code')
      .not('latitude', 'is', null)
      .not('longitude', 'is', null)
      .order('name');

    if (error) {
      console.error('Error fetching known courses:', error);
      return [];
    }

    return data.map(course => ({
      name: course.name,
      lat: parseFloat(course.latitude),
      lon: parseFloat(course.longitude),
      country: course.country_code
    }));
  } catch (error) {
    console.error('Error getting known courses:', error);
    return [];
  }
}

/**
 * Check if a golf course has coordinates available (for autocomplete weather indicators)
 */
export function isGolfCourseKnown(courseName) {
  if (!courseName) return false;
  
  // Check cache first for quick response
  const cacheKey = normalizeCourseName(courseName);
  if (coordinateCache.has(cacheKey)) {
    return coordinateCache.get(cacheKey) !== null;
  }
  
  // For autocomplete purposes, we'll do an async check in the background
  // and return false immediately to avoid blocking the UI
  getDatabaseCourseCoordinates(courseName).then(() => {
    // This will cache the result for future quick lookups
  }).catch(error => {
    console.warn('Background coordinate check failed:', error);
  });
  
  return false; // Return false immediately for autocomplete performance
}

export default {
  getGolfCourseCoordinates,
  batchGeocode,
  formatCoordinates,
  getDistance,
  isGeocodingAvailable,
  addKnownCourse,
  getKnownCourses,
  isGolfCourseKnown
};